import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { FaChevronDown, FaPlus } from 'react-icons/fa6';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import HoverButton from '../../../buttons/HoverButton';
import useSelectedCommunity from '../../../../../hooks/community/useSelectedCommunity';
import useCommunities from '../../../../../hooks/community/useCommunities';
import { BUTTON_STYLE, LABEL_STYLE } from './style';
import { communityTypeIcons } from '../../../../../utils/iconUtils';
import Icon from '../../../icons/Icon';
import { CommunityRole } from '../../../../../types/community';
import {
  setSelectedCommunity,
  setSelectedDashboard,
  wipeSelectedCommunity,
} from '../../../../../redux/slices/applicationSlice';

export default function CommunityButton() {
  const community = useSelectedCommunity();
  const communities = useCommunities();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  if (!community) return null;

  const handleCommunitySelect = (communityId: number, dashboardId: number) => {
    dispatch(wipeSelectedCommunity());
    dispatch(setSelectedCommunity(communityId));
    dispatch(setSelectedDashboard(dashboardId));
  };

  return (
    <HoverButton hoverable={false}>
      <HoverButton.Button>
        <div className="flex w-fit items-center justify-between gap-2 rounded-[8px] px-3 py-[8px] text-[16px] transition-all hover:bg-secondary-200 group-[.expanded]:bg-secondary-200">
          <div className="flex items-center gap-2">
            <Icon
              icon={communityTypeIcons[community.type]}
              className="h-5 w-5 flex-shrink-0 text-primary"
            />
            <span className="text-gray-500">{t('general.community')}</span>
            <span className="font-medium text-primary-900">{community.name}</span>
          </div>
          <FaChevronDown className="h-3.5 w-3.5 text-primary-300" />
        </div>
      </HoverButton.Button>
      <HoverButton.Div className="mt-2 flex max-h-[75vh] w-full min-w-[250px] flex-col gap-2 overflow-x-hidden overflow-y-scroll rounded-[12px] border border-secondary-200 bg-surface p-3 shadow-lg">
        <div className="w-full text-sm font-semibold text-primary-900">
          {t('general.community')}
        </div>
        <div className="w-full border-t border-secondary-200" />
        {(communities?.filter((b) => b.dashboard && b.role === CommunityRole.ADMIN) ?? []).map(
          (b) => (
            <div
              key={b.dashboard!.dashboardId}
              onClick={() => handleCommunitySelect(b.id!, b.dashboard!.dashboardId)}
              className={twMerge(
                BUTTON_STYLE,
                'cursor-pointer items-center justify-between px-3 py-2 leading-[19px]',
                classNames({
                  'bg-primary-100 text-primary-900 hover:bg-opacity-100': community.id === b.id,
                }),
              )}>
              <div className="flex w-full items-center gap-2">
                <Icon icon={communityTypeIcons[b.type]} className="h-5 w-5 flex-shrink-0" />
                <p
                  className={twMerge(
                    LABEL_STYLE,
                    'w-full overflow-hidden text-ellipsis pr-2 font-medium',
                  )}>
                  {b.name}
                </p>
              </div>
              {community.id === b.id && (
                <FaCheck className="h-3 w-3 flex-shrink-0 text-primary-900" />
              )}
            </div>
          ),
        )}
        <div className="w-full border-t border-secondary-200" />
        <div className="flex w-full flex-shrink-0 flex-col">
          <Link to="/onboarding" className={twMerge(BUTTON_STYLE, 'gap-2 px-3 py-2 text-sm')}>
            <FaPlus className="h-4 w-4 text-primary-300" />
            <p className="text-sm font-medium leading-[19px] text-primary-900">
              {t('page.communityOverview.new')}
            </p>
          </Link>
        </div>
      </HoverButton.Div>
    </HoverButton>
  );
}
